<template>
  <!-- Overlay -->
  <div class="relative w-full">
    <div
      class="fixed bottom-12 right-auto w-full xl:w-auto justify-center xl:absolute xl:right-0 xl:bottom-auto"
    >
      <MapZoom></MapZoom>
    </div>
    <div
      v-if="mapSettingsStore.showLargeFleets"
      class="fixed bottom-16 pb-3 xl:pb-0 right-auto w-full xl:w-auto flex justify-end xl:absolute xl:right-0 xl:top-8 pointer-events-none"
    >
      <MapFleets></MapFleets>
    </div>
    <SettleLegend v-if="shipActionStore.pendingAction === 'settle'" />
    <MapTargetHud />
    <MapSideBar />
    <!-- Top Bar -->
  </div>
  <!-- WebGL Container -->
  <div
    class="webgl-container content-height-limit-map z-10"
    ref="container"
    @contextmenu.prevent
  ></div>
  <MapToolTip :tool-tip-visible="mapStore.toolTipVisible"></MapToolTip>
  <MapPlayerNameToolTip></MapPlayerNameToolTip>
  <AreaSelectionOptions v-if="mapStore.areaActionSelecting" />
  <div class="absolute bottom-2 left-1 hidden md:block">
    {{ mapStore.mouseXPos }} / {{ mapStore.mouseYPos }}
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import * as THREE from 'three'
import { useMapStore } from '@/stores/mapStore.ts'
import type MapCoordinate from '@/types/MapCoordinate.ts'
import emitter from '@/models/eventBus.ts'
import MapToolTip from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapToolTip.vue'
import MapZoom from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapZoom.vue'
import MapFleets from '@/components/Sections/Map/MapFleets.vue'
import { usePlanetStore } from '@/stores/planetStore.ts'
import { ShipActionPath } from '@/models/Map/MapShipActionPath.ts'
import { MapAllShipsActionPaths } from '@/models/Map/MapAllShipsActionPaths.ts'
import { MapPortalConnections } from '@/models/Map/MapPortalConnections.ts'
import MapTargetHud from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapTargetHud.vue'
import { useShipActionStore } from '@/stores/shipActionStore.ts'
import { useRoute } from 'vue-router'
import type PlayerRelation from '@/types/PlayerRelation.ts'
import { MapMouseController } from '@/models/Map/MapMouseController.ts'
import { useShipStore } from '@/stores/shipStore.ts'
import { useFleetStore } from '@/stores/fleetStore.ts'
import { useMapCoordinateStore } from '@/stores/mapCoordinateStore.ts'
import { useMapShipStore } from '@/stores/mapShipStore.ts'
import { useMapPlanetOwnerStore } from '@/stores/mapPlanetOwnerStore.ts'
import { useMapPlanetStore } from '@/stores/mapPlanetStore.ts'
import { useMapStarStore } from '@/stores/mapStarStore.ts'
import { useMapPendingSettleStore } from '@/stores/mapPendingSettleStore.ts'
import AreaSelectionOptions from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/AreaSelectionOptions.vue'
import MapPlayerNameToolTip from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapPlayerNameToolTip.vue'
import { MapBlackHole } from '@/models/Map/MapBlackHole.ts'
import MapSideBar from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapSideBar/MapSideBar.vue'
import SettleLegend from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/SettleLegend.vue'
import { MapCrossHairInit } from '@/models/Map/MapCrosshair.ts'
import { useMapPortalStore } from '@/stores/mapPortalStore.ts'
import { usePortalStore } from '@/stores/portalStore.ts'
import { MapScanRadius } from '@/models/Map/MapScanRadius.ts'
import { useMapSettingsStore } from '@/stores/mapSettingsStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'

// Store references and states
const mapStore = useMapStore()
const mapCoordinateStore = useMapCoordinateStore()
const mapShipStore = useMapShipStore()
const mapPlanetOwnerStore = useMapPlanetOwnerStore()
const mapPlanetStore = useMapPlanetStore()
const mapStarStore = useMapStarStore()
const mapPendingSettleStore = useMapPendingSettleStore()
const mapPortalStore = useMapPortalStore()
const mapSettingsStore = useMapSettingsStore()
const gameStore = useGameStore()

const shipStore = useShipStore()
const fleetStore = useFleetStore()
const planetStore = usePlanetStore()
const portalStore = usePortalStore()
const shipActionStore = useShipActionStore()
const container = ref<HTMLDivElement | null>(null)
const toolTipVisible = ref(false)
const route = useRoute()

// The mouseController sets up event listeners in its constructor and manages mouse/touch interactions
// It doesn't need to be called directly but must be instantiated for the map controls to work
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let mouseController: MapMouseController
let scene: THREE.Scene
let camera: THREE.PerspectiveCamera
let renderer: THREE.WebGLRenderer
let mapScanRadius: MapScanRadius
let allShipsActionPaths: MapAllShipsActionPaths
let portalConnections: MapPortalConnections
let isContextLost = false

/**
 * Handle WebGL context loss (common on mobile Safari)
 */
const handleContextLost = (event: Event) => {
  event.preventDefault()
  isContextLost = true
  console.warn('WebGL context lost - rendering paused')
}

/**
 * Handle WebGL context restoration
 */
const handleContextRestored = () => {
  isContextLost = false
  console.log('WebGL context restored - rendering resumed')

  // Force re-render after context restoration
  if (renderer && scene && camera) {
    renderer.render(scene, camera)
  }
}

/**
 * Initialize the map
 */
onMounted(async () => {
  try {
    /**
     * Create the WebGL renderer and camera
     */
    renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
      alpha: true
    })
    
    if (container.value) {
      renderer.setSize(container.value.clientWidth, container.value.clientHeight)
      container.value.appendChild(renderer.domElement)
    }

    // Handle WebGL context loss/restore (critical for mobile Safari)
    renderer.domElement.addEventListener('webglcontextlost', handleContextLost, false)
    renderer.domElement.addEventListener('webglcontextrestored', handleContextRestored, false)
  } catch (error) {
    // Handle WebGL renderer creation errors
    gameStore.showModal('error', error)
    return
  }

  camera = new THREE.PerspectiveCamera(
    45,
    container.value ? container.value.clientWidth / container.value.clientHeight : 1,
    1,
    500
  )
  camera.position.set(0, 0, 400)
  camera.lookAt(0, 0, 0)

  camera.position.x = 1000
  camera.position.y = 1000
  
  // Initialize camera position in mapStore
  mapStore.cameraXPos = camera.position.x
  mapStore.cameraYPos = camera.position.y

  scene = new THREE.Scene()
  mapStore.scene = scene
  mapStore.camera = camera
  
  // Set scene to sort objects during rendering for proper transparency
  renderer.sortObjects = true

  /** Store the scene in the mapCoordinateStore */
  mapCoordinateStore.scene = scene

  /** Load the Instanced Meshes */
  mapPlanetOwnerStore.initPlanetOwnerInstancedMesh(10000) // Small
  if (mapPlanetOwnerStore.planetOwnerInstancedMesh) {
    scene.add(mapPlanetOwnerStore.planetOwnerInstancedMesh)
  }

  mapPlanetStore.initPlanetInstancedMesh(10000) // Small
  if (mapPlanetStore.planetInstancedMesh) {
    scene.add(mapPlanetStore.planetInstancedMesh)
  }
  
  mapStarStore.initStarInstancedMesh(1200)
  if (mapStarStore.starInstancedMesh) {
    scene.add(mapStarStore.starInstancedMesh)
  }
  
  // Initialize ships after stars so they render on top
  mapShipStore.initShipInstancedMesh(20000) // Enough?
  if (mapShipStore.shipInstancedMesh) {
    scene.add(mapShipStore.shipInstancedMesh)
  }

  mapPendingSettleStore.initObjectInstancedMesh(10000)
  if (mapPendingSettleStore.objectInstancedMesh) {
    scene.add(mapPendingSettleStore.objectInstancedMesh)
  }

  mapPortalStore.initPortalInstancedMesh(100)
  if (mapPortalStore.portalInstancedMesh) {
    scene.add(mapPortalStore.portalInstancedMesh)
  }

  /** Tests **/
  // Test objects are created and immediately removed to verify instanced mesh functionality
  mapPlanetOwnerStore.addPlanetOwner(-0, -0, 0x000000)
  const planetInstanceId = mapPlanetStore.addPlanet(-0, -0, 1, 5)
  const starInstanceId = mapStarStore.addStar(-0, -0, 1, 4)
  mapPendingSettleStore.addObject(-0, -0, 0x000000)
  const portalInstanceId = mapPortalStore.addPortal(-0, -0)
  mapShipStore.addShip(-0, -0, 0x000000)
  
  /**
   * Remove test objects
   */
  mapPlanetStore.removePlanet(planetInstanceId)
  mapStarStore.removeStar(starInstanceId)
  mapPortalStore.removePortal(portalInstanceId)

  /**
   * Add black hole
   */
  new MapBlackHole(scene)

  /**
   * Initialize scan radius visualization
   */
  mapScanRadius = new MapScanRadius(scene)

  /**
   * CrossHairs
   */
  new MapCrossHairInit(scene, camera)

  /**
   * Init Ship Action Path
   */
  mapStore.shipActionPath = new ShipActionPath(scene)

  /**
   * Init All Ships Action Paths
   */
  allShipsActionPaths = new MapAllShipsActionPaths(scene)

  /**
   * Init Portal Connections
   */
  portalConnections = new MapPortalConnections(scene)

  /**
   * Init Mouse Controller
   */
  if (container.value) {
    mouseController = new MapMouseController(container.value, camera, scene, renderer)
  }

  // Listen to mapCoordinateUpdate events from Websockets
  emitter.on('mapCoordinateUpdate', (data) => {
    mapCoordinateStore.process(data as MapCoordinate)
    // Update scan radii when map coordinates change
    if (mapScanRadius) {
      mapScanRadius.updateScanRadii()
    }
  })

  // Listen to mapRelationUpdate events from Websockets
  emitter.on('mapRelationUpdate', (data) => {
    mapCoordinateStore.reDrawPlayerAssets((data as PlayerRelation).playerId)
  })

  /**
   * Listen to mouse events
   */
  if (container.value) {
    window.addEventListener('resize', resizeRenderer)
  }

  // Backplane for detecting coordinates in space
  // Use a larger size to ensure it covers the entire galaxy grid plus some buffer
  const backPlaneSize = Math.max(gameStore.galaxyTileGridSize * 2, 10000)
  const geometry = new THREE.PlaneGeometry(backPlaneSize, backPlaneSize)
  const material = new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0
  })
  const plane = new THREE.Mesh(geometry, material)
  plane.name = 'backPlane'
  plane.position.z = -0.2
  scene.add(plane)

  /**
   * Start the animation loop
   */
  let clock = new THREE.Clock()
  let targetFPS = 60 // Target FPS
  let frameDuration = 1 / targetFPS
  let timeSinceLastFrame = 0

  function animate() {
    requestAnimationFrame(animate)

    // Skip rendering if WebGL context is lost
    if (isContextLost) {
      return
    }

    // Calculate the time elapsed since last frame
    let delta = clock.getDelta()
    timeSinceLastFrame += delta

    // Update ship pulsating effect
    mapShipStore.updateTime(delta)

    // Only render if enough time has passed
    if (timeSinceLastFrame > frameDuration) {
      renderer.render(scene, camera)
      camera.updateProjectionMatrix()
      timeSinceLastFrame = 0 // Reset the frame time
    }
  }

  animate()

  /**
   * Pan to the coordinates if set
   */
  if (planetStore.planets.size > 0) {
    const firstPlanet = planetStore.planets.values().next().value
    if (firstPlanet) {
      mapStore.gotoX = firstPlanet.xPos
      mapStore.gotoY = firstPlanet.yPos
    }
  }

  /**
   * Load the visible sectors
   */
  mapStore.loadVisibleSectors()

  renderer.render(scene, camera)
  camera.updateProjectionMatrix()
})

/**
 * Dispose the renderer and remove event listeners
 */
onUnmounted(() => {
  if (renderer) {
    // Remove WebGL context event listeners
    renderer.domElement.removeEventListener('webglcontextlost', handleContextLost)
    renderer.domElement.removeEventListener('webglcontextrestored', handleContextRestored)
    renderer.dispose()
  }
  if (container.value) {
    window.removeEventListener('resize', resizeRenderer)
  }
  if (allShipsActionPaths) {
    allShipsActionPaths.dispose()
  }
  if (portalConnections) {
    portalConnections.dispose()
  }
})

/**
 * Resize the renderer when the window is resized
 */
const resizeRenderer = () => {
  if (container.value && renderer) {
    if (container.value.clientWidth === 0 || container.value.clientHeight === 0) {
      return
    }
    renderer.setSize(container.value.clientWidth, container.value.clientHeight)
    camera.aspect = container.value.clientWidth / container.value.clientHeight
  }
}

watch(
  () => shipActionStore.requestMapReset,
  (newValue) => {
    if (newValue) {
      mapStore.shipActionPath.resetActionPath()
    }
  }
)

/**
 * Watch the zoom factor and update the camera
 */
watch(
  () => mapStore.zoomFactor,
  (newValue) => {
    if (camera) {
      camera.zoom = newValue
      mapCoordinateStore.applyZoomFactorAll(newValue)
      if (mapScanRadius) {
        mapScanRadius.applyZoomFactor(newValue)
      }
      if (mapStore.shipActionPath) {
        mapStore.shipActionPath.updateZoom(newValue)
      }
      if (allShipsActionPaths) {
        allShipsActionPaths.onZoomChanged(newValue)
      }
    }
  }
)

/**
 * Watch the gotoX and gotoY values and move the camera
 */
const gotoCoordinates = computed(() => ({
  x: mapStore.gotoX,
  y: mapStore.gotoY
}))

watch(
  gotoCoordinates,
  (newCoords) => {
    if (newCoords.x === 0 && newCoords.y === 0) {
      return
    }
    if (!camera) {
      return
    }
    //move camera to new coordinates
    camera.position.x = newCoords.x
    camera.position.y = newCoords.y
    mapStore.cameraXPos = camera.position.x
    mapStore.cameraYPos = camera.position.y
    toolTipVisible.value = false

    mapStore.gotoX = 0
    mapStore.gotoY = 0
    mapStore.crossHairVisible = true
  },
  { deep: true }
)

/**
 * Watch the mouse position and update the store
 */
watch(
  () => route.name,
  (newValue) => {
    if (newValue?.toString().startsWith('MapGalaxy')) {
      // Resize the renderer with delay
      setTimeout(() => {
        resizeRenderer()
      }, 100)
    }
  }
)

/**
 * Rerender the path
 */
watch(
  () => shipActionStore.redrawPath,
  () => {
    if (!shipActionStore.redrawPath) {
      return
    }
    mapStore.shipActionPath.removePendingAction()
    mapStore.shipActionPath.setActionPath()
    shipActionStore.redrawPath = false
  }
)

watch(
  () => shipActionStore.pendingAction,
  (newValue, oldValue) => {
    if (newValue === 'settle') {
      //Mark all planets that are targeted for settlement
      const settleTargets = shipStore.getAllCoordsTargetedForSettlement()
      for (let i = 0; i < settleTargets.length; i++) {
        const target = settleTargets[i]
        const instanceId = mapPendingSettleStore.addObject(target.x, target.y, 0xffffff)
        mapPendingSettleStore.scaleAndPositionObject(
          instanceId,
          target.x,
          target.y,
          mapStore.zoomFactor
        )
      }
      //Mark all planets that can be settled
      const planets = mapCoordinateStore.getSettleablePlanets()

      for (let i = 0; i < planets.length; i++) {
        const target = planets[i]
        //Check if the coordinate is already in the settleTargets array
        if (
          settleTargets.find(
            (coord: { x: number; y: number }) => coord.x === target.xPos && coord.y === target.yPos
          )
        ) {
          continue
        }

        //Define color
        let color = 0x000000
        if (target.compatibility === 'A' || target.compatibility === 'F') {
          color = 0x00ff00 // Light Green
        } else if (target.compatibility === 'B') {
          color = 0xadd8e6 // Light blue
        } else if (target.compatibility === 'C') {
          continue
        } else if (target.compatibility === 'D') {
          color = 0xffff00 // Yellow
        } else if (target.compatibility === 'E') {
          color = 0xff0000 // Red
        }

        const instanceId = mapPendingSettleStore.addObject(target.xPos, target.yPos, color)
        mapPendingSettleStore.scaleAndPositionObject(
          instanceId,
          target.xPos,
          target.yPos,
          mapStore.zoomFactor
        )
      }
    } else if (oldValue === 'settle') {
      //Hide all planets that are targeted for settlement
      mapPendingSettleStore.clearObjects()
    }
  }
)

/**
 * Watch the showScanRange setting and update scan radii
 */
watch(
  () => mapSettingsStore.showScanRange,
  () => {
    if (mapScanRadius) {
      mapScanRadius.updateScanRadii()
    }
  }
)

/**
 * Watch the showAllShipActionPaths setting and toggle display
 */
watch(
  () => mapSettingsStore.showAllShipActionPaths,
  (newValue) => {
    if (allShipsActionPaths) {
      allShipsActionPaths.setVisible(newValue)
    }
  }
)

/**
 * Watch for ship store changes and update action paths
 */
watch(
  () => shipStore.ships,
  () => {
    if (allShipsActionPaths) {
      allShipsActionPaths.onShipsUpdated()
    }
  },
  { deep: true }
)

/**
 * Watch for fleet store changes and update action paths
 */
watch(
  () => fleetStore.fleets,
  () => {
    if (allShipsActionPaths) {
      allShipsActionPaths.onShipsUpdated()
    }
  },
  { deep: true }
)

/**
 * Watch for portal store changes and update connections
 */
watch(
  () => portalStore.portals,
  () => {
    if (portalConnections) {
      portalConnections.onPortalsUpdated()
    }
  },
  { deep: true }
)

</script>

<style scoped>
.webgl-container {
  width: 100%;
}
</style>
